import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/hooks/useAuth";
import { DashboardLayout } from "@/components/dashboard/DashboardLayout";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import { Download, FileText, Calculator, AlertTriangle } from "lucide-react";
import { generateTaxSummary, generate1099Forms, estimateTaxLiability } from "@/services/taxService";
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";

export default function TaxDashboard() {
  const { user } = useAuth();
  const currentYear = new Date().getFullYear();
  const [selectedYear, setSelectedYear] = useState(currentYear);

  const { data: athleteProfile } = useQuery({
    queryKey: ["athlete-profile", user?.id],
    enabled: !!user,
    queryFn: async () => {
      const { data, error } = await supabase
        .from("athlete_profiles")
        .select("athlete_id")
        .eq("user_id", user!.id)
        .single();

      if (error) throw error;
      return data;
    },
  });

  const { data: taxSummary, isLoading } = useQuery({
    queryKey: ["tax-summary", athleteProfile?.athlete_id, selectedYear],
    enabled: !!athleteProfile?.athlete_id,
    queryFn: () => generateTaxSummary(athleteProfile!.athlete_id, selectedYear),
  });

  const { data: taxLiability } = useQuery({
    queryKey: ["tax-liability", taxSummary?.totalCompensation],
    enabled: !!taxSummary,
    queryFn: () => estimateTaxLiability(taxSummary!.totalCompensation),
  });

  const { data: taxDocuments } = useQuery({
    queryKey: ["tax-documents", athleteProfile?.athlete_id, selectedYear],
    enabled: !!athleteProfile?.athlete_id,
    queryFn: async () => {
      const { data, error } = await supabase
        .from("tax_documents")
        .select("*")
        .eq("athlete_id", athleteProfile!.athlete_id)
        .eq("tax_year", selectedYear)
        .order("generated_at", { ascending: false });

      if (error) throw error;
      return data;
    },
  });

  const handleGenerate1099 = async () => {
    if (!athleteProfile) return;

    try {
      const result = await generate1099Forms(athleteProfile.athlete_id, selectedYear);
      toast.success("1099 Forms Generated", {
        description: `Generated ${result.forms.length} 1099-NEC forms`,
      });
    } catch (error) {
      toast.error("Failed to generate 1099 forms", {
        description: error instanceof Error ? error.message : "Unknown error",
      });
    }
  };

  return (
    <DashboardLayout>
      <div className="space-y-6">
        <div className="flex justify-between items-center">
          <div>
            <h1 className="text-3xl font-bold">Tax Dashboard</h1>
            <p className="text-muted-foreground">
              Track NIL earnings and tax obligations
            </p>
          </div>
          <Select
            value={selectedYear.toString()}
            onValueChange={(value) => setSelectedYear(parseInt(value))}
          >
            <SelectTrigger className="w-[180px]">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {[currentYear, currentYear - 1, currentYear - 2].map((year) => (
                <SelectItem key={year} value={year.toString()}>
                  {year}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        {taxSummary && taxSummary.needs1099 && (
          <Alert variant="destructive">
            <AlertTriangle className="h-4 w-4" />
            <AlertTitle>1099 Threshold Met</AlertTitle>
            <AlertDescription>
              Your earnings exceed $600. You'll receive 1099-NEC forms from your payers.
              Consult a tax professional for guidance.
            </AlertDescription>
          </Alert>
        )}

        <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-4">
          <Card>
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
              <CardTitle className="text-sm font-medium">Total Compensation</CardTitle>
              <FileText className="h-4 w-4 text-muted-foreground" />
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold">
                ${taxSummary?.totalCompensation.toLocaleString() || 0}
              </div>
              <p className="text-xs text-muted-foreground">
                {selectedYear} Tax Year
              </p>
            </CardContent>
          </Card>

          <Card>
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
              <CardTitle className="text-sm font-medium">Cash Earnings</CardTitle>
              <FileText className="h-4 w-4 text-muted-foreground" />
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold">
                ${taxSummary?.totalCashCompensation.toLocaleString() || 0}
              </div>
              <p className="text-xs text-muted-foreground">
                Direct payments
              </p>
            </CardContent>
          </Card>

          <Card>
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
              <CardTitle className="text-sm font-medium">Number of Payers</CardTitle>
              <FileText className="h-4 w-4 text-muted-foreground" />
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold">
                {taxSummary?.payerCount || 0}
              </div>
              <p className="text-xs text-muted-foreground">
                Different brands/collectives
              </p>
            </CardContent>
          </Card>

          <Card>
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
              <CardTitle className="text-sm font-medium">Estimated Tax</CardTitle>
              <Calculator className="h-4 w-4 text-muted-foreground" />
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold">
                ${taxLiability?.totalEstimated.toLocaleString() || 0}
              </div>
              <p className="text-xs text-muted-foreground">
                Federal, state, SE tax
              </p>
            </CardContent>
          </Card>
        </div>

        <Card>
          <CardHeader>
            <CardTitle>Quarterly Earnings Breakdown</CardTitle>
            <CardDescription>Income by quarter for {selectedYear}</CardDescription>
          </CardHeader>
          <CardContent>
            {isLoading ? (
              <div className="text-center py-8">Loading...</div>
            ) : taxSummary && taxSummary.quarterlyBreakdown.length > 0 ? (
              <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-4">
                {taxSummary.quarterlyBreakdown.map((quarter) => (
                  <div key={quarter.quarter} className="p-4 border rounded-lg">
                    <div className="font-semibold">{quarter.quarter}</div>
                    <div className="text-2xl font-bold mt-2">
                      ${quarter.totalCompensation.toLocaleString()}
                    </div>
                    <div className="text-sm text-muted-foreground mt-1">
                      {quarter.dealCount} deals
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <div className="text-center py-8 text-muted-foreground">
                No earnings recorded for {selectedYear}
              </div>
            )}
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <div className="flex justify-between items-center">
              <div>
                <CardTitle>1099 Forms</CardTitle>
                <CardDescription>Tax documents from payers</CardDescription>
              </div>
              <Button onClick={handleGenerate1099}>
                <FileText className="mr-2 h-4 w-4" />
                Generate 1099s
              </Button>
            </div>
          </CardHeader>
          <CardContent>
            {taxDocuments && taxDocuments.length > 0 ? (
              <div className="space-y-2">
                {taxDocuments.map((doc: any) => (
                  <div key={doc.id} className="flex items-center justify-between p-3 border rounded-lg">
                    <div>
                      <div className="font-medium">{doc.form_type}</div>
                      <div className="text-sm text-muted-foreground">
                        ${Number(doc.total_compensation).toLocaleString()} • 
                        Generated {new Date(doc.generated_at).toLocaleDateString()}
                      </div>
                    </div>
                    <Button variant="outline" size="sm">
                      <Download className="mr-2 h-4 w-4" />
                      Download
                    </Button>
                  </div>
                ))}
              </div>
            ) : (
              <div className="text-center py-8 text-muted-foreground">
                No tax documents generated yet
              </div>
            )}
          </CardContent>
        </Card>
      </div>
    </DashboardLayout>
  );
}